import React, { useContext } from 'react';

import { client } from '@admin/libraries/apollo';
import { ClutterGeoEnum, useNightTransportPlannerFacilitiesQuery, WarehouseOrDepotFragment } from '@admin/schema';

import { Context } from './context';
import { Mode } from './filters';
import { InlineSelectFormGroup } from './inline_select_form_group';
import { WarehouseOrDepotKind } from './types';

export const WarehouseOrDepotFormGroup: React.FC<{
  name: string;
  label: string;
  disabled?: boolean;
  currentWarehouseOrDepot?: WarehouseOrDepotFragment;
  warehouseOrDepotKinds?: WarehouseOrDepotKind[];
  showForm?: boolean;
  onChangeWarehouseOrDepot(_: WarehouseOrDepotFragment): void;
}> = ({
  name: formGroupName,
  label,
  disabled,
  currentWarehouseOrDepot,
  warehouseOrDepotKinds = [WarehouseOrDepotKind.Warehouse, WarehouseOrDepotKind.Depot],
  showForm = false,
  onChangeWarehouseOrDepot,
}) => {
  const { mode, geo, loading } = useContext(Context);
  const { data } = useNightTransportPlannerFacilitiesQuery({
    client,
    variables: { filters: { geo: ClutterGeoEnum[geo! as keyof typeof ClutterGeoEnum], active: true } },
    skip: !geo,
  });

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const [type, resourceID] = event.target.value.split('-');
    const facility: WarehouseOrDepotFragment = (() => {
      switch (type) {
        case WarehouseOrDepotKind.Warehouse:
          return data?.warehouseOrDepots.find(
            ({ id, __typename }) => id === resourceID && __typename === WarehouseOrDepotKind.Warehouse,
          );
        case WarehouseOrDepotKind.Depot:
          return data?.warehouseOrDepots.find(
            ({ id, __typename }) => id === resourceID && __typename === WarehouseOrDepotKind.Depot,
          );
      }
    })()!;

    onChangeWarehouseOrDepot(facility);
  };

  const value = (() => {
    switch (currentWarehouseOrDepot?.__typename) {
      case WarehouseOrDepotKind.Warehouse:
        return `${WarehouseOrDepotKind.Warehouse}-${currentWarehouseOrDepot.id}`;
      case WarehouseOrDepotKind.Depot:
        return `${WarehouseOrDepotKind.Depot}-${currentWarehouseOrDepot.id}`;
    }
  })();

  if (mode === Mode.Viewing && !showForm) {
    return (
      <p>
        {label}: {currentWarehouseOrDepot?.name}
      </p>
    );
  }

  return (
    <InlineSelectFormGroup
      value={value ?? ''}
      label={label}
      onChange={onChange}
      name={formGroupName}
      disabled={!data || mode !== Mode.Editing || disabled || loading}
    >
      <option disabled={!!currentWarehouseOrDepot} value="">
        - Facility -
      </option>

      <optgroup
        label={WarehouseOrDepotKind.Warehouse}
        disabled={warehouseOrDepotKinds && !warehouseOrDepotKinds.includes(WarehouseOrDepotKind.Warehouse)}
      >
        {data?.warehouseOrDepots
          .filter(({ __typename }) => __typename === WarehouseOrDepotKind.Warehouse)
          .map(({ id, name }) => (
            <option value={`${WarehouseOrDepotKind.Warehouse}-${id}`} key={id}>
              {name}
            </option>
          ))}
      </optgroup>

      <optgroup
        label={WarehouseOrDepotKind.Depot}
        disabled={warehouseOrDepotKinds && !warehouseOrDepotKinds.includes(WarehouseOrDepotKind.Depot)}
      >
        {data?.warehouseOrDepots
          .filter(({ __typename }) => __typename === WarehouseOrDepotKind.Depot)
          .map(({ id, name }) => (
            <option value={`${WarehouseOrDepotKind.Depot}-${id}`} key={id}>
              {name}
            </option>
          ))}
      </optgroup>
    </InlineSelectFormGroup>
  );
};
